import {Injectable} from '@angular/core';

export interface BadgeItem {
  type: string;
  value: string;
}

export interface ChildrenItems {
  state: string;
  target?: boolean;
  name: string;
  type?: string;
  children?: ChildrenItems[];
  rolesCanView: string;
}

export interface MainMenuItems {
  state: string;
  short_label?: string;
  main_state?: string;
  target?: boolean;
  name: string;
  type: string;
  icon: string;
  badge?: BadgeItem[];
  children?: ChildrenItems[];
  rolesCanView: string;
}

export interface Menu {
  label: string;
  main: MainMenuItems[];
}

const MENUITEMS = [
  {
    label: '',
    main: [
      {
        state: 'dashboard',
        short_label: 'D',
        name: 'Dashboard',
        type: 'link',
        icon: 'ti-home',
        rolesCanView: 'gr_admin,administrator,clerk,communications,leadership',
      },
      {
        state: 'contributions',
        short_label: 'C',
        name: 'Contributions',
        type: 'sub',
        icon: 'ti-gift',
        rolesCanView: 'gr_admin,administrator,clerk,communications',
        children: [
          {
            state: 'cash-and-check',
            name: 'Cash and Check',
            rolesCanView: 'gr_admin,administrator,clerk,communications',
            tooltip: 'Record cash and check contributions including Qualified Charitable Distribution (QCD) and Donor-Advised Fund (DAF) contributions'
          },
          {
            state: 'credit-card',
            name: 'Credit Card',
            rolesCanView: 'gr_admin,administrator,clerk,communications',
            tooltip: 'Record credit card contributions'
          },
          {
            state: 'bank-transfer',
            name: 'Bank Transfer',
            rolesCanView: 'gr_admin,administrator,clerk,communications',
            tooltip: 'Record bank transfer (ACH) contributions'
          },
          {
            state: 'marketable-securities',
            name: 'Marketable Securities',
            rolesCanView: 'gr_admin,clerk,communications',
            tooltip: 'Record appreciated securities contributions'
          },
          {
            state: 'non-cash',
            name: 'Non-Cash',
            rolesCanView: 'gr_admin,clerk,communications',
            tooltip: 'Record non-cash contributions'
          },
        ]
      },
      {
        state: 'pledges',
        short_label: 'P',
        name: 'Pledges',
        type: 'link',
        icon: 'ti-money',
        rolesCanView: 'gr_admin,clerk',
      },
      {
        state: 'setup',
        short_label: 'S',
        name: 'Setup',
        type: 'sub',
        icon: 'ti-settings',
        rolesCanView: 'gr_admin,administrator,clerk,communications',
        children: [
          {
            state: 'designations',
            name: 'Designations',
            rolesCanView: 'gr_admin,administrator,clerk',
            tooltip: 'Add or edit designations'
          },
          {
            state: 'ledger-accounts',
            name: 'Ledger Accounts',
            rolesCanView: 'gr_admin,administrator,clerk',
            tooltip: 'Add or edit general ledger accounts'
          },
          {
            state: 'donors',
            name: 'Donors',
            rolesCanView: 'gr_admin,clerk',
            tooltip: 'Add or edit donors'
          },
          {
            state: 'gifts-services',
            name: 'Gifts/Services for Donor',
            rolesCanView: 'gr_admin,administrator,clerk',
            tooltip: 'Add or edit gifts and services provided to donors'
          },
          {
            state: 'general-preferences',
            name: 'General Preferences',
            rolesCanView: 'gr_admin,administrator,communications',
            tooltip: 'Add or edit non-profit preferences'
          },
          //{
          //  state: 'sponsored-trips',
          //  name: 'Sponsored Trips'
          //},
        ]
      },
      {
        state: 'statements',
        short_label: 'S',
        name: 'Statements',
        type: 'sub',
        icon: 'ti-pencil-alt',
        rolesCanView: 'gr_admin,administrator,clerk,communications',
        children: [
          {
            state: 'view-statements',
            name: 'Donor Statements',
            rolesCanView: 'gr_admin,clerk,communications',
            tooltip: 'Generate and print/email statements'
          },
          {
            state: 'designation-preferences',
            name: 'Designation Preferences',
            rolesCanView: 'gr_admin,administrator,communications',
            tooltip: 'Edit how designations are presented on donor statements'
          },
        ]
      },
      {
        state: 'reports',
        short_label: 'R',
        name: 'Reports',
        type: 'sub',
        icon: 'ti-bar-chart',
        rolesCanView: 'gr_admin,administrator,clerk,communications,leadership',
        children: [
          {
            state: 'giving',
            name: 'Giving',
            rolesCanView: 'gr_admin,administrator,clerk,communications,leadership',
            tooltip: 'Define and generate Giving Report'
          },
          {
            state: 'development',
            name: 'Development',
            rolesCanView: 'gr_admin,administrator,clerk,communications,leadership',
            tooltip: 'Define and generate Development Report'
          },
          {
            state: 'pledges-report',
            name: 'Pledges',
            rolesCanView: 'gr_admin,administrator,clerk,communications,leadership',
            tooltip: 'Define and generate Pledges Report'
          },
       ]
      },
      {
        state: 'admin',
        short_label: 'A',
        name: 'Admin',
        type: 'sub',
        icon: 'ti-user',
        rolesCanView: 'gr_admin,administrator,clerk',
        children: [
          {
            state: 'user-management',
            name: 'User Management',
            rolesCanView: 'gr_admin,administrator',
            tooltip: 'Add, edit or delete users and assign security roles'
          },
          {
            state: 'import-data',
            name: 'Import Data',
            rolesCanView: 'gr_admin,clerk',
            tooltip: 'Import donors and cash/check contributions'
          },
          // We thought we'd need this page but haven't had need for it yet.
          //{
          //  state: 'nonprofit-info',
          //  name: 'Non-Profit Info',
          //  rolesCanView: 'gr_admin,administrator,clerk,leadership',
          //},
       ]
      },
      {
        state: 'training',
        short_label: 'T',
        name: 'Training',
        type: 'link',
        icon: 'ti-light-bulb',
        rolesCanView: 'gr_admin,administrator,clerk,communications,leadership',
      },
      {
        state: 'gift-reporter-admin',
        short_label: 'A',
        name: 'GiftReporter Admin',
        type: 'sub',
        icon: 'ti-key',
        rolesCanView: 'gr_admin',
        children: [
          {
            state: 'nonprofit-management',
            name: 'Non-Profit Admin',
            rolesCanView: 'gr_admin',
            tooltip: 'Add new non-profits and edit existing organization information'
          },
       ]
      },
    ]
  }
];

@Injectable()
export class MenuItems {
  getAll(): Menu[] {
    return MENUITEMS;
  }
}
